import type {OptimizedSchedule, Schedule} from '@domain/schedule'
import {useCurrentScheduleQuery, OptimizerScheduleStatus} from '@domain/schedule'
import {usePlanningChartStartEnd} from '@widgets/planning-schedule-chart'
import {useMemo} from 'react'

import {useOptimizedSchedulesAndRuns} from '../../../hooks'
import {OptimizerRun, OptimizerScheduleType} from '../../../types'

export type OptimizerSchedule =
  | ({type: OptimizerScheduleType.Manual} & Schedule)
  | ({type: OptimizerScheduleType.Optimized; isLatestAcceptedSchedule: boolean} & OptimizedSchedule)
  | ({type: OptimizerScheduleType.Calculating} & OptimizerRun)

export function useOptimizerPlanOverview(plantCode: string) {
  const {startOfPlan, endOfPlan} = usePlanningChartStartEnd()

  const {data: manualSchedule} = useCurrentScheduleQuery({
    plantId: plantCode,
    range: [startOfPlan, endOfPlan]
  })
  const {data: optimizedSchedulesAndRuns} = useOptimizedSchedulesAndRuns()

  if (!manualSchedule || !optimizedSchedulesAndRuns)
    throw new Error('Manual schedule and optimized schedules and runs must be loaded beforehand')

  return useMemo<OptimizerSchedule[]>(() => {
    const currentManualSchedule = {...manualSchedule, type: OptimizerScheduleType.Manual}

    const latestAcceptedSchedule = optimizedSchedulesAndRuns.optimizedSchedules
      .filter((schedule) => schedule.status === OptimizerScheduleStatus.Accepted)
      .sort(
        (a, b) => new Date(a.lastSaved.savedAt).getTime() - new Date(b.lastSaved.savedAt).getTime()
      )
      .pop()

    const optimizedSchedules = optimizedSchedulesAndRuns.optimizedSchedules
      .map((schedule) => ({
        ...schedule,
        isLatestAcceptedSchedule: schedule.scheduleId === latestAcceptedSchedule?.scheduleId,
        type: OptimizerScheduleType.Optimized
      }))
      .sort(
        (a, b) =>
          new Date(a.optimizationDetails.triggeredOn).getTime() -
          new Date(b.optimizationDetails.triggeredOn).getTime()
      )

    const optimizedRuns = optimizedSchedulesAndRuns.optimizerRuns.map((run) => ({
      ...run,
      type: OptimizerScheduleType.Calculating
    }))

    const optimizerSchedules = [
      currentManualSchedule,
      ...optimizedSchedules,
      ...optimizedRuns
    ].filter(Boolean) as OptimizerSchedule[]

    return optimizerSchedules
  }, [
    manualSchedule,
    optimizedSchedulesAndRuns?.optimizedSchedules,
    optimizedSchedulesAndRuns?.optimizerRuns
  ])
}
